import { useEffect, useState } from "react";
import {
  ADDRESS_BOOK_LIMITS,
  AddressBookError,
  emptyAddressBook,
  newGroupId,
  normalizeAddressBook,
  sealAddressBook,
  type AddressBook,
} from "@timecapsule/crypto";
import { api, ApiError } from "../api";
import { openStoredAddressBook } from "../addressBookCarry";
import { link } from "../App";
import { isKeyRequestCancelled, useSession } from "../session";
import { errorText, useT } from "../i18n";
import { useRefreshHandler } from "../Refresh";
import { useShowMore } from "../pagedList";
import { ShowMore } from "../ShowMore";

/**
 * Address book (PLAN.md 4.14): aliases for members I follow and named groups of them. The whole
 * book is one NIP-44 payload encrypted to my own key in this browser; the server keeps it and
 * cannot read it. Opening and saving need the key; Lock hides the opened book at once.
 */
export function Contacts() {
  const t = useT();
  const { nameOfId, followed, withKey, lockGeneration, lockStillValid, keyStatus } = useSession();
  const [book, setBook] = useState<AddressBook | null>(null);
  const [version, setVersion] = useState(0);
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  const [newGroup, setNewGroup] = useState("");

  // Lock hides the opened book (same rule as opened private capsules).
  const visible = book !== null && openedAt !== null && lockStillValid(openedAt);
  useEffect(() => {
    if (openedAt !== null && !lockStillValid(openedAt)) {
      setBook(null);
      setOpenedAt(null);
      setDirty(false);
    }
  }, [lockGeneration, openedAt, lockStillValid]);

  async function open() {
    setError(null);
    setUnreadable(false);
    setBusy(true);
    const generation = lockGeneration;
    try {
      const opened = await withKey(t.contacts.purposeOpen, (key) => openStoredAddressBook(key));
      setBook(opened.book);
      setVersion(opened.version);
      setOpenedAt(generation);
      setDirty(false);
    } catch (err) {
      if (err instanceof AddressBookError) setUnreadable(true);
      else if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
    setBusy(false);
  }
  useRefreshHandler(async () => {
    // Preserve edits and never ask for a private key from a general Refresh click.
    if (busy) return;
    if (dirty) {
      if ((await api.addressBook()).version !== version) setError(t.contacts.changedElsewhere);
    } else if (visible && keyStatus === "unlocked") await open();
  });

  /**
   * No key of this account opens the stored book (an earlier key that is not in the keyring): start
   * an empty one over it. Adding that key's phrase under Keys -> Keyring opens and moves it instead.
   */
  async function startOver() {
    const stored = await api.addressBook();
    setBook(emptyAddressBook());
    setVersion(stored.version);
    setOpenedAt(lockGeneration);
    setDirty(true);
    setUnreadable(false);
  }

  async function save() {
    if (!book) return;
    setError(null);
    setBusy(true);
    try {
      const clean = normalizeAddressBook(book);
      const blob = await withKey(t.contacts.purposeSave, async (key) => sealAddressBook(clean, key));
      setVersion((await api.saveAddressBook(version, blob)).version);
      setBook(clean);
      setDirty(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === "address_book_changed") setError(t.contacts.changedElsewhere);
      else if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
    setBusy(false);
  }

  const edit = (fn: (b: AddressBook) => AddressBook) => {
    if (!book) return;
    setBook(fn(book));
    setDirty(true);
  };

  const people = [...followed.keys()].sort((a, b) => nameOfId(a).localeCompare(nameOfId(b)));
  // Up to 500 followed members: shown 50 at a time (PLAN.md 4.20).
  const peoplePage = useShowMore(people);

  return (
    <div>
      <div className="page-head">
        <h1>{t.contacts.title}</h1>
        {visible && book && (
          <button className="primary small" disabled={!dirty || busy} onClick={() => void save()}>
            {dirty ? t.contacts.save : t.contacts.saved}
          </button>
        )}
      </div>
      <p className="small muted">{t.contacts.lead}</p>

      {!visible && (
        <>
          <button className="primary" disabled={busy} onClick={() => void open()}>
            {t.contacts.open}
          </button>
          {unreadable && (
            <div className="warn small">
              <p>{t.contacts.unreadable}</p>
              <p>
                <a href="/keys" onClick={link("/keys")}>{t.contacts.addOldPhrase}</a>
              </p>
              <button onClick={() => { if (confirm(t.contacts.startOverConfirm)) void startOver(); }}>{t.contacts.startOver}</button>
            </div>
          )}
        </>
      )}

      {visible && book && (
        <div className="contacts">
          <section>
          <h2>{t.contacts.groups}</h2>
          {book.groups.length === 0 && <p className="small muted">{t.contacts.noGroups}</p>}
          {book.groups.length > 0 && (
            <ul className="rows">
              {book.groups.map((g) => (
                <li key={g.id}>
                  <span className="grow">
                    <input
                      aria-label={t.contacts.groupName}
                      value={g.name}
                      maxLength={ADDRESS_BOOK_LIMITS.groupName}
                      onChange={(e) => edit((b) => ({ ...b, groups: b.groups.map((x) => (x.id === g.id ? { ...x, name: e.target.value } : x)) }))}
                    />
                  </span>
                  <span className="muted small">{t.contacts.memberCount(g.members.length)}</span>
                  <button
                    className="link small"
                    onClick={() => confirm(t.contacts.confirmDelete(g.name)) && edit((b) => ({ ...b, groups: b.groups.filter((x) => x.id !== g.id) }))}
                  >
                    {t.contacts.deleteGroup}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <form
            className="form-row"
            onSubmit={(e) => {
              e.preventDefault();
              const name = newGroup.trim();
              if (!name || book.groups.length >= ADDRESS_BOOK_LIMITS.groups) return;
              edit((b) => ({ ...b, groups: [...b.groups, { id: newGroupId(), name, members: [] }] }));
              setNewGroup("");
            }}
          >
            <label>
              {t.contacts.newGroup}
              <input value={newGroup} maxLength={ADDRESS_BOOK_LIMITS.groupName} placeholder={t.contacts.newGroupHint} onChange={(e) => setNewGroup(e.target.value)} />
            </label>
            <button type="submit" disabled={!newGroup.trim()}>
              {t.contacts.addGroup}
            </button>
          </form>
          </section>

          <section>
          <h2>{t.contacts.people}</h2>
          {people.length === 0 && (
            <p className="small muted">
              {t.contacts.nobody}{" "}
              <a href="/account" onClick={link("/account")}>
                {t.contacts.toAccount}
              </a>
            </p>
          )}
          {people.length > 0 && (
            <ul className="rows">
              {peoplePage.shown.map((id) => (
                <li key={id}>
                  <div className="contact">
                    <b>{nameOfId(id)}</b>
                    <input
                      aria-label={t.contacts.alias}
                      value={book.aliases[String(id)] ?? ""}
                      maxLength={ADDRESS_BOOK_LIMITS.alias}
                      placeholder={t.contacts.aliasHint}
                      onChange={(e) => edit((b) => ({ ...b, aliases: { ...b.aliases, [String(id)]: e.target.value } }))}
                    />
                    <div className="chips">
                      {book.groups.map((g) => {
                        const inGroup = g.members.includes(id);
                        return (
                          <button
                            key={g.id}
                            type="button"
                            className={inGroup ? "chip on" : "chip"}
                            onClick={() =>
                              edit((b) => ({
                                ...b,
                                groups: b.groups.map((x) =>
                                  x.id !== g.id ? x : { ...x, members: inGroup ? x.members.filter((m) => m !== id) : [...x.members, id] },
                                ),
                              }))
                            }
                          >
                            {g.name || "…"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <ShowMore hasMore={peoplePage.hasMore} onMore={peoplePage.showMore} />

          <button className="primary" disabled={!dirty || busy} onClick={() => void save()}>
            {dirty ? t.contacts.save : t.contacts.saved}
          </button>
          </section>
        </div>
      )}
      {error && <p className="error small">{error}</p>}
    </div>
  );
}
